import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthContextType } from '../types/auth';
import authService from '../services/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Check for existing token on mount
    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            // Verify token and fetch user
            authService
                .getCurrentUser()
                .then((userData) => {
                    setUser(userData);
                })
                .catch((error) => {
                    console.error('Failed to fetch user:', error);
                    localStorage.removeItem('auth_token');
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, []);

    const login = async (token: string) => {
        localStorage.setItem('auth_token', token);

        // Fetch user data
        try {
            const userData = await authService.getCurrentUser();
            setUser(userData);
        } catch (error) {
            console.error('Failed to fetch user after login:', error);
            localStorage.removeItem('auth_token');
            throw error;
        }
    };

    const logout = () => {
        authService.logout().catch(console.error);
        localStorage.removeItem('auth_token');
        setUser(null);
    };

    const hasPermission = (permission: string): boolean => {
        if (!user) return false;
        if (user.role === 'admin' || user.permissions.includes('*')) return true;
        return user.permissions.includes(permission);
    };

    const isAdmin = (): boolean => {
        return user?.role === 'admin' || false;
    };

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isGuest: !user,
        login,
        logout,
        hasPermission,
        isAdmin,
        isLoading,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
